import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { decodeJwt } from 'jose';
import { environment } from '../../environments/environment';
import { GlobalResponse } from '../models/global-response';
import { SKIP_GLOBAL_ERROR } from '../http-context';
import { OrgContextDTO } from '../models/organization-dto';
import { orgUrl } from '../org-url';
import { ActAsService } from './act-as.service';

export interface LoginResponse {
  token: string;
  refreshToken: string;
  roleIds: number[];
  /** True when this CMS sign-in used a password under the staff floor. The
   *  CMS forces a rotation before any other page. */
  mustRotatePassword?: boolean;
  /** Present for ORG STAFF sign-ins only: which org this account belongs
   *  to, for branding and the org-tier navigation. Platform staff get
   *  null. */
  orgContext?: OrgContextDTO | null;
}

/** Mirrors UserRoleEnum in the backend. */
const SUPER_ADMIN = 1;
const ADMIN = 3;
const SUPPORT = 4;
const MARKETING = 6;
const EDITOR = 7;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private baseUrl = `${environment.apiUrl}/authorization`;

  readonly #actAs = inject(ActAsService);

  constructor(private http: HttpClient, private router: Router) {}

  getAccessToken(): string | null {
    return localStorage.getItem('token');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  isLoggedIn(): boolean {
    return !!this.getAccessToken();
  }

  /** Whether to *offer* an action only a super admin may take. The API is
   *  still the gate; this only stops the CMS showing buttons it knows the
   *  server will refuse. The JWT carries no roles, so they come from the
   *  sign-in response. */
  isSuperAdmin(): boolean {
    return this.#roles().includes(SUPER_ADMIN);
  }

  /** Mirrors ADMIN_ROLES in the backend's authz module: the roles allowed
   *  to change anything a user eventually sees. */
  isAdmin(): boolean {
    const roles = this.#roles();
    return roles.includes(SUPER_ADMIN) || roles.includes(ADMIN);
  }

  /** Mirrors SUPPORT_ROLES in the backend's authz module: the roles that
   *  may work user accounts (tickets, notifications inspector). */
  isSupport(): boolean {
    return this.isAdmin() || this.#roles().includes(SUPPORT);
  }

  /** Mirrors MARKETING_ROLES in the backend's authz module: the roles that
   *  run the outbound channels (broadcasts, notification and email
   *  templates). */
  isMarketing(): boolean {
    return this.isAdmin() || this.#roles().includes(MARKETING);
  }

  /** Mirrors CONTENT_ROLES in the backend's authz module: the roles that
   *  may write articles, categories and Learn content. Everyone else with
   *  CMS access reads them. */
  isContentEditor(): boolean {
    return this.isAdmin() || this.#roles().includes(EDITOR);
  }

  #roles(): number[] {
    try {
      const stored = JSON.parse(localStorage.getItem('roleIds') ?? '[]');
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  }

  /** The org an ORG-STAFF session belongs to, else null. Stored at
   *  sign-in; the refresh response does not carry it, so it survives
   *  refreshes untouched. Display/branding only; the backend principal
   *  is the real scope. */
  orgContext(): OrgContextDTO | null {
    try {
      const raw = localStorage.getItem('orgContext');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return typeof parsed?.orgId === 'number' ? parsed : null;
    } catch {
      return null;
    }
  }

  isOrgStaff(): boolean {
    return this.orgContext() !== null;
  }

  /** Repair the cached context from a source that knows better, the
   *  bootstrap /me call. Writing null clears it, which is how a demoted
   *  account stops seeing an org console it no longer belongs to. */
  adoptOrgContext(context: OrgContextDTO | null | undefined): void {
    if (context) {
      localStorage.setItem('orgContext', JSON.stringify(context));
    } else {
      localStorage.removeItem('orgContext');
    }
  }

  /** Repair the cached roles from the bootstrap /me call: they were
   *  stored at sign-in, so a role changed since then kept offering a menu
   *  the API refuses. Absent (an older backend) leaves the cache alone. */
  adoptRoles(roleIds: number[] | undefined): void {
    if (Array.isArray(roleIds)) {
      localStorage.setItem('roleIds', JSON.stringify(roleIds));
    }
  }

  /** Where this session belongs. Org staff have no platform dashboard to
   *  land on, and their console is not a muufree URL: it lives under their
   *  own /o/<slug> base. Every redirect in the CMS asks this instead of
   *  writing '/dashboard' and stranding them. */
  homeUrl(): string {
    const context = this.orgContext();
    return context ? orgUrl(context.slug) : '/dashboard';
  }

  /** The signed-in account id, from the token's `sub`. The JWT carries
   *  nothing a person would recognise, so names and emails still come from
   *  /admin/users/me, but this is enough to know WHOSE they should be, and
   *  therefore when a cached profile belongs to somebody else. */
  accountId(): string | null {
    const token = this.getAccessToken();
    if (!token) return null;
    try {
      return decodeJwt(token).sub ?? null;
    } catch {
      return null;
    }
  }

  login(identifier: string, password: string, returnUrl?: string): Observable<GlobalResponse<LoginResponse>> {
    // admin/login is the CMS door: same credentials as the app's /login, but
    // accounts without a CMS role are refused instead of handed a token.
    return this.http.post<GlobalResponse<LoginResponse>>(`${this.baseUrl}/admin/login`, { identifier, password },
      // The form shows the failure inline; no modal over it.
      { context: new HttpContext().set(SKIP_GLOBAL_ERROR, true) }).pipe(
      tap(res => {
          if(res.result){
        this.storeSession(res.result);
        // A sub-floor staff password has to be rotated before anything else;
        // every other destination waits behind that one screen.
        if (this.mustRotatePassword()) {
          this.router.navigateByUrl('/first-sign-in');
          return;
        }
        // Org staff land inside their own wall, on their own base; the
        // org guard would bounce them off every platform page anyway.
        if (res.result.orgContext) {
          this.router.navigateByUrl(orgUrl(res.result.orgContext.slug));
          return;
        }
        // Honour where the guard/interceptor was sending the user before it
        // bounced them here; fall back to the dashboard.
        this.router.navigateByUrl(this.safeReturnUrl(returnUrl));
          }
      })
    );
  }

  /** The authoritative rotation demand for this session, persisted across a
   *  refresh so the forced screen cannot be skipped by reloading. */
  mustRotatePassword(): boolean {
    return localStorage.getItem('mustRotate') === 'true';
  }

  /** Only trust an in-app path: a leading single slash, never a protocol-
   *  relative `//host` or an absolute URL, and never back to `/login`. */
  private safeReturnUrl(returnUrl?: string): string {
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')
        && !returnUrl.startsWith('/login')) {
      return returnUrl;
    }
    return this.homeUrl();
  }

  /** A session worth bouncing an already-signed-in user off /login for:
   *  a token that is still valid, or one a refresh token can revive. Mirrors
   *  authGuard so the two can never disagree and loop. */
  hasLiveSession(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    if (this.getRefreshToken()) return true;
    try {
      const { exp } = decodeJwt(token);
      return !exp || exp > Math.floor(Date.now() / 1000);
    } catch {
      return false;
    }
  }

  /** Redeem a staff invite code and sign straight in. The backend endpoint
   *  is the forgot-password reset. Invites go only to accounts with no
   *  password yet; the backend refuses a code redeemed onto an account that
   *  already has one, which signs in through the normal form instead. */
  redeemInvite(
    identifier: string, code: string, newPassword: string
  ): Observable<void> {
    return this.http.post<GlobalResponse<LoginResponse>>(
      `${this.baseUrl}/forgot-password/reset`,
      { identifier, newPassword, verificationCode: code },
      // The invite form shows failures inline; no modal over it.
      { context: new HttpContext().set(SKIP_GLOBAL_ERROR, true) }
    ).pipe(
      map(res => {
        if (!res.result?.token) {
          throw new Error('redeem returned no token');
        }
        this.storeSession(res.result);
        // Same rule as login: org staff belong inside their own wall, and
        // every platform page would bounce them anyway.
        this.router.navigateByUrl(this.homeUrl());
      })
    );
  }

  /** Trades the stored refresh token for a new access token. The backend
   *  rotates: the old refresh token is revoked and a new one comes back, so
   *  this must never run twice concurrently; the interceptor owns that. */
  refresh(): Observable<string> {
    const refreshToken = this.getRefreshToken();
    return this.http.post<GlobalResponse<LoginResponse>>(`${this.baseUrl}/refresh`, { refreshToken },
      // A failed refresh signs the user out; a modal on top of that is noise.
      { context: new HttpContext().set(SKIP_GLOBAL_ERROR, true) }).pipe(
      map(res => {
        if (!res.result?.token) {
          throw new Error('refresh returned no token');
        }
        this.storeSession(res.result, true);
        return res.result.token;
      })
    );
  }

  /** Adopt a freshly-minted token pair without navigating, e.g. after a
   *  password change, which revokes the old session server-side and hands
   *  back a replacement the current tab must switch to.
   *
   *  The org context is KEPT: changing a password never changes which
   *  organization you are staff of, and dropping it here left org staff on
   *  the platform dashboard with no way back into their own console. */
  applySession(session: LoginResponse): void {
    this.storeSession(session, true);
  }

  private storeSession(
    session: LoginResponse, keepOrgContext = false
  ): void {
    localStorage.setItem('token', session.token);
    localStorage.setItem('refreshToken', session.refreshToken);
    localStorage.setItem('roleIds', JSON.stringify(session.roleIds ?? []));
    // Every session write refreshes the flag: a login sets it, and the
    // token pair a completed rotation returns clears it (the backend sends
    // false once the new password meets the floor).
    localStorage.setItem(
      'mustRotate', session.mustRotatePassword ? 'true' : 'false'
    );
    // A refresh response carries no orgContext, so it must not wipe the
    // one the sign-in stored; a fresh login always overwrites (null for
    // platform staff removes any stale org).
    if (!keepOrgContext) {
      if (session.orgContext) {
        localStorage.setItem(
          'orgContext', JSON.stringify(session.orgContext)
        );
      } else {
        localStorage.removeItem('orgContext');
      }
    }
  }

  logout(): Observable<any> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.clearTokens();
      return new Observable(observer => observer.complete());
    }

    return this.http.post(`${this.baseUrl}/logout`, { refreshToken }).pipe(
      tap(() => this.clearTokens())
    );
  }

  clearTokens(): void {
    // Roles go with the tokens: a stale set left behind would offer the
    // next signed-in user actions their account cannot take.
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('roleIds');
    localStorage.removeItem('mustRotate');
    localStorage.removeItem('orgContext');
    // An act-as outlives the token unless it is stopped here: it is
    // sessionStorage, so signing out and back in as somebody else in the
    // SAME tab kept sending X-Org-Context, which every account but a
    // super admin is refused for, on every request.
    this.#actAs.stop();
    this.router.navigate(['/login']);
  }
}
